import { useQuery } from '@tanstack/react-query';
import { changeRequestQueries } from '../api/change-request-queries';
import type {
  ChangeRequestFilters,
  ChangeRequestPageParams,
} from '../api/change-request-api';
import { CHANGE_REQUESTS_PAGE_SIZE } from '../config/change-requests';
import type { ChangeRequest, ChangeRequestEntityKind } from './schema';

/**
 * One page of the merchant's moderation queue. No scope argument: the backend
 * derives the merchant from the caller's token.
 */
export function useChangeRequests(
  filters: ChangeRequestFilters,
  page: ChangeRequestPageParams,
) {
  return useQuery(changeRequestQueries.list(filters, page));
}

/**
 * The PENDING request queuing a given entity, or `undefined` when none is.
 *
 * The catalog card knows a product is queued (its `lifecycle_status`) but not
 * WHICH request queued it, and `POST /change-requests/{id}/cancel` is addressed
 * by the request's id. `GET /change-requests/by-merchant` has no `entity_id`
 * filter, so the pending page is read and matched here instead.
 *
 * Reading the pending tab's FIRST page is deliberate: it shares its query key
 * with the «Заявки» screen, so a merchant who has been there pays nothing, and
 * a cancel invalidates both at once. The cap is the same page the queue screen
 * shows — a merchant sitting on more pending requests than that has a queue to
 * work through, and the «Заявки» screen is where that is done.
 */
export function usePendingChangeRequestFor(
  entityKind: ChangeRequestEntityKind,
  entityId: string | undefined,
): ChangeRequest | undefined {
  const { data } = useQuery({
    ...changeRequestQueries.list(
      { status: 'pending' },
      { limit: CHANGE_REQUESTS_PAGE_SIZE, offset: 0 },
    ),
    enabled: entityId !== undefined,
  });

  if (entityId === undefined) return undefined;
  return data?.items.find(
    (item) => item.entityKind === entityKind && item.entityId === entityId,
  );
}
